import {createPortal} from 'preact/compat';
import {useCallback, useMemo, useState} from 'preact/hooks';

import {PortalProvider} from '~/components/PortalProvider';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {useOpenTelemetry} from '~/foundation/OpenTelemetry/hooks';
import {useRootProvider} from '~/foundation/RootProvider/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import {useElementEventListener} from '~/hooks/useElementEventListener';
import type {ShowToastEvent} from '~/types/event';
import type {Toast} from '~/types/toast';
import {isoDocument} from '~/utils/document';
import register from '~/utils/register';

import {ShopToastElement} from './ShopToastElement';
import type {
  ShopToastManagerProps,
  ShopToastManagerWebComponentProps,
} from './types';

const ShopToastManagerInternal = () => {
  const {instanceId} = useRootProvider();
  const {trackPageImpression} = useMonorail();
  const [toastQ, setQueue] = useState<Toast[]>([]);
  const {recordCounter} = useOpenTelemetry();

  const removeFromQueue = useCallback((toastId: string) => {
    setQueue((prevQueue) => prevQueue.filter((t) => t.id !== toastId));
  }, []);

  const addToQueue = useCallback((toast: Toast) => {
    setQueue((prevQueue) => [...prevQueue, toast]);
  }, []);

  const elementEventListeners = useMemo(() => {
    const renderToast = ({detail}: ShowToastEvent) => {
      // emit statsD event if flow is present
      if (detail.flow) {
        recordCounter('shop_js_toast_rendered', {
          attributes: {
            flow: detail.flow,
            variant: detail.variant || 'default',
          },
        });
      }
      // emit the event for this with the traceId used in the login flow
      trackPageImpression({
        allowDuplicates: true,
        analyticsTraceId: detail.analyticsTraceId,
        flow: detail.flow,
        page: 'RENDER_TOAST',
      });
      addToQueue(detail);
    };
    return {
      renderToast,
    };
  }, [addToQueue, recordCounter, trackPageImpression]);

  useElementEventListener(elementEventListeners);

  return createPortal(
    <PortalProvider instanceId={instanceId} type="toast" variant="default">
      <div className="shop-toast-manager fixed bottom-8 left-0 right-0 z-max mx-auto flex flex-col items-center gap-3">
        {toastQ.map((t) => (
          <ShopToastElement key={t.id} {...t} onClose={removeFromQueue} />
        ))}
      </div>
    </PortalProvider>,
    isoDocument.body,
  );
};

register<ShopToastManagerProps>(
  ({element, ...props}) => (
    <RootProvider element={element} featureName="ShopToastManager">
      <ShopToastManagerInternal {...props} />
    </RootProvider>
  ),
  {
    methods: ['renderToast'],
    name: 'shop-toast-manager',
    props: {},
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-toast-manager']: ShopToastManagerWebComponentProps;
    }
  }
}

export function ShopToastManager(props: ShopToastManagerWebComponentProps) {
  return <shop-toast-manager {...props} />;
}
